// Payroll - Salary Holds Management Tab (Finance & Command view)
import React, { useState, useEffect } from 'react';
import { hasAnyRole } from '../../utils/roleHelper';
import PersonnelSelect from './PersonnelSelect';

const SalaryHoldsTab = ({ api, user, armyTypeId, armyTypeName }) => {
  const [holds, setHolds] = useState([]);
  const [personnelList, setPersonnelList] = useState([]);
  const [filter, setFilter] = useState('active'); // 'active', 'released', 'all'
  const [placeForm, setPlaceForm] = useState({ army_personnel_id: '', reason: '' });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [showPlaceForm, setShowPlaceForm] = useState(false);

  const canPlaceHold = hasAnyRole(user, ['admin', 'commander', 'unit_commander', 'department_commander', 'army_commander', 'finance_director']);
  const canRelease = hasAnyRole(user, ['admin', 'commander', 'unit_commander', 'department_commander', 'army_commander', 'finance_director', 'accountant_general']);
  const canView = hasAnyRole(user, ['admin', 'finance_director', 'finance_officer', 'director_general', 'accountant_general', 'commander', 'unit_commander', 'department_commander', 'army_commander']);

  useEffect(() => { fetchData(); }, [armyTypeId, filter]);

  const fetchData = async () => {
    try {
      const [hRes, pRes] = await Promise.all([
        api.get(`/salary_holds?army_type_id=${armyTypeId}${filter !== 'all' ? `&hold_status=${filter}` : ''}`),
        api.get(`/army_personnel?army_type_id=${armyTypeId}`)
      ]);
      setHolds(hRes?.salary_holds || hRes?.data?.salary_holds || []);
      setPersonnelList(pRes?.army_personnel || pRes?.data?.army_personnel || []);
    } catch (e) { console.error(e); }
  };

  const handlePlaceHold = async (e) => {
    e.preventDefault();
    if (!placeForm.army_personnel_id) return setMsg({ text: 'Please select a personnel member', type: 'warning' });
    if (!placeForm.reason.trim()) return setMsg({ text: 'A reason is required for placing a hold', type: 'warning' });
    setLoading(true); setMsg({ text: '', type: '' });
    try {
      const res = await api.post('/salary_holds', {
        army_personnel_id: placeForm.army_personnel_id,
        reason: placeForm.reason
      });
      if (res?.response && !res.response.ok) {
        setMsg({ text: res.data?.error || 'Failed to place hold', type: 'error' });
      } else {
        setMsg({ text: '✅ Salary hold placed successfully. Salary will be held on the next payroll run.', type: 'success' });
        setPlaceForm({ army_personnel_id: '', reason: '' });
        setShowPlaceForm(false);
        fetchData();
      }
    } catch (e) { setMsg({ text: 'Error placing hold', type: 'error' }); }
    finally { setLoading(false); }
  };

  const handleRelease = async (holdId, name) => {
    const reason = prompt(`Enter reason for releasing salary hold for ${name}:`);
    if (!reason) return;
    setLoading(true);
    try {
      const res = await api.delete(`/salary_holds/${holdId}`, { release_reason: reason });
      if (res?.response && !res.response.ok) {
        setMsg({ text: res.data?.error || 'Failed to release hold', type: 'error' });
      } else {
        setMsg({ text: `✅ Salary hold released for ${name}`, type: 'success' });
        fetchData();
      }
    } catch (e) { setMsg({ text: 'Error releasing hold', type: 'error' }); }
    finally { setLoading(false); }
  };

  const activeHolds = holds.filter(h => h.hold_status === 'active');
  const msgBg = msg.type === 'error' ? '#fee2e2' : msg.type === 'warning' ? '#fffbeb' : '#dcfce7';
  const msgColor = msg.type === 'error' ? '#991b1b' : msg.type === 'warning' ? '#92400e' : '#166534';

  return (
    <div>
      {/* Info Banner */}
      <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, fontSize: 13, color: '#92400e' }}>
        <strong>⚠ Salary Hold/Release Mechanism:</strong> Salary holds are placed by the Unit Commander for unauthorized absences or disciplinary actions.
        Finance Director / Accountant General can also place or release holds. A held salary remains frozen until explicitly released — it is <strong>not</strong> pro-rated.
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Active Holds', value: activeHolds.length, color: '#dc2626', bg: '#fee2e2' },
          { label: 'Total Personnel', value: personnelList.length, color: '#1b4332', bg: '#dcfce7' },
          { label: '% On Hold', value: personnelList.length > 0 ? `${((activeHolds.length / personnelList.length) * 100).toFixed(1)}%` : '0%', color: '#92400e', bg: '#fef3c7' },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.bg}`, borderRadius: 8, padding: '14px 18px', textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: s.color, fontWeight: 700 }}>{s.label}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: s.color, marginTop: 4 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {msg.text && <div style={{ marginBottom: 16, padding: '10px 14px', background: msgBg, borderRadius: 6, color: msgColor, fontSize: 13, fontWeight: 600 }}>{msg.text}</div>}

      {/* Actions Bar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Filter Tabs */}
        <div style={{ display: 'flex', gap: 0, border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
          {[
            { key: 'active', label: '🔴 Active Holds' },
            { key: 'released', label: '✅ Released' },
            { key: 'all', label: '📋 All History' },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              style={{ padding: '7px 16px', border: 'none', background: filter === f.key ? '#1b4332' : '#fff', color: filter === f.key ? '#fff' : '#374151', fontWeight: filter === f.key ? 700 : 500, cursor: 'pointer', fontSize: 12 }}>
              {f.label}
            </button>
          ))}
        </div>
        {canPlaceHold && (
          <button onClick={() => setShowPlaceForm(!showPlaceForm)}
            style={{ padding: '7px 16px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>
            {showPlaceForm ? '✕ Cancel' : '🔒 Place New Salary Hold'}
          </button>
        )}
        <button onClick={fetchData} style={{ padding: '7px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>🔄 Refresh</button>
      </div>

      {/* Place Hold Form */}
      {showPlaceForm && canPlaceHold && (
        <div style={{ background: '#fff', border: '1px solid #fca5a5', borderRadius: 8, padding: 20, marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: '#dc2626' }}>🔒 Place Salary Hold</h3>
          <form onSubmit={handlePlaceHold} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '2 1 280px' }}>
              <PersonnelSelect
                personnelList={personnelList}
                value={placeForm.army_personnel_id}
                required={true}
                onChange={(pId) => setPlaceForm({ ...placeForm, army_personnel_id: pId })}
              />
            </div>
            <div style={{ flex: '3 1 300px' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Reason for Hold *</label>
              <input required value={placeForm.reason} onChange={e => setPlaceForm({ ...placeForm, reason: e.target.value })}
                placeholder="e.g. Unauthorized absence — 3 days without leave approval"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }} />
            </div>
            <button type="submit" disabled={loading}
              style={{ padding: '9px 20px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
              {loading ? 'Placing Hold...' : '🔒 Confirm Hold'}
            </button>
          </form>
          <p style={{ margin: '10px 0 0', fontSize: 12, color: '#6b7280' }}>
            ⚠ This will prevent the personnel's salary from being paid on the next payroll run. The hold remains active until released.
          </p>
        </div>
      )}

      {/* Holds Table */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              {['Personnel', 'Rank', 'Reason', 'Held By', 'Held Date', 'Status', 'Release Info', ...(canRelease ? ['Actions'] : [])].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 12, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {holds.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#9ca3af' }}>
                {filter === 'active' ? 'No active salary holds for this force. ✅' : 'No hold records found.'}
              </td></tr>
            )}
            {holds.map((h, i) => (
              <tr key={h.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                <td style={{ padding: '10px 14px', fontWeight: 700 }}>
                  {h.army_personnel?.full_name || `${h.army_personnel?.first_name || ''} ${h.army_personnel?.last_name || ''}`}
                  <div style={{ fontSize: 10, color: '#9ca3af' }}>{h.army_personnel?.army_id}</div>
                </td>
                <td style={{ padding: '10px 14px', color: '#6b7280' }}>{h.army_personnel?.rank || '—'}</td>
                <td style={{ padding: '10px 14px', maxWidth: 200 }}>
                  <div style={{ fontSize: 12 }}>{h.reason || '—'}</div>
                </td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: '#6b7280' }}>{h.held_by?.email || '—'}</td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: '#6b7280' }}>
                  {h.held_at ? new Date(h.held_at).toLocaleDateString() : '—'}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{
                    padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                    background: h.hold_status === 'active' ? '#fee2e2' : '#dcfce7',
                    color: h.hold_status === 'active' ? '#dc2626' : '#166534'
                  }}>
                    {h.hold_status === 'active' ? '🔴 ACTIVE' : '✅ RELEASED'}
                  </span>
                </td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: '#6b7280' }}>
                  {h.hold_status === 'released' ? (
                    <>
                      <div>{h.release_reason || 'Hold released'}</div>
                      {h.released_at && <div style={{ fontSize: 10, marginTop: 2 }}>by {h.released_by?.email} — {new Date(h.released_at).toLocaleDateString()}</div>}
                    </>
                  ) : '—'}
                </td>
                {canRelease && (
                  <td style={{ padding: '10px 14px' }}>
                    {h.hold_status === 'active' && (
                      <button onClick={() => handleRelease(h.id, h.army_personnel?.full_name || `${h.army_personnel?.first_name} ${h.army_personnel?.last_name}`)}
                        style={{ padding: '4px 12px', background: '#059669', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                        ✅ Release Hold
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SalaryHoldsTab;
